import React, { useEffect, useCallback, useState } from "react";
import { useIntl, FormattedMessage } from "react-intl";
import { useFormikContext } from "formik";
import { Box, Text, Stack, Link, useDisclosure, Button, Flex } from "@chakra-ui/react";
import { useTheme } from "@chakra-ui/system";
import * as env from "env";
import useGetPrimaryEmail from "api/accounts/useGetPrimaryEmail";
import { TextFieldGroup } from "components/Form";
import EmailVerification from "components/EmailVerification";
import ExternalEmailModLink from "components/ExternalEmailModLink";
import { useSettings } from "utils/settingsContext";

import { REQUIRES_EMAIL_VERIFICATION, INVALID_EMAIL } from "../schema";
import { NextButton, UpdateEmailButton, VerifyEmailButton } from "./Buttons";

import { HiOutlinePencil } from "react-icons/hi";

export const EmailSection = ({ onComplete }) => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const { integratorSettings } = useSettings();
  const disablePrimaryEmailUpdateByUser = integratorSettings?.feature_flags?.disablePrimaryEmailUpdateByUser || false;
  const [isEditState, setIsEditState] = useState(true);
  const { onOpen, isOpen, onClose } = useDisclosure();
  const { values, setFieldValue, errors, setFieldTouched, setFieldError } = useFormikContext();
  const { data, isSuccess, isError, refetch } = useGetPrimaryEmail();

  const onSuccess = useCallback(() => {
    setFieldError("email", null);
    refetch();
    onComplete();
  }, [onComplete, refetch, setFieldError]);

  const validateEmailInputWithPrimary = (inputValue) => {
    if (isSuccess) {
      const isEmailInputChanged = data?.payload?.email !== inputValue;
      return (isEmailInputChanged || !data.payload.is_verified) && REQUIRES_EMAIL_VERIFICATION;
    }
  };

  const validateExistingPrimaryEmail = () => {
    if (isError) {
      return REQUIRES_EMAIL_VERIFICATION;
    }
  };

  const validateEmailField = (input) => {
    const validators = [validateExistingPrimaryEmail, validateEmailInputWithPrimary];
    return validators
      .map((validator) => validator(input))
      .find((validation) => typeof validation === "string");
  };

  useEffect(() => {
    if (isSuccess) {
      setFieldTouched("email", Boolean(data?.payload?.is_verified));
      setFieldValue("email", data?.payload?.email);
      setFieldError("email", null);
      setIsEditState(!disablePrimaryEmailUpdateByUser && !data?.payload?.is_verified); // if user editability is disabled don't allow at all, otherwise check if field is verified or not
    }
  }, [
    data?.payload?.email,
    isSuccess,
    setFieldValue,
    setFieldTouched,
    setFieldError,
    data?.payload?.is_verified,
    disablePrimaryEmailUpdateByUser,
  ]);

  const setEditModeTrue = useCallback(() => {
    setIsEditState(true);
  }, [setIsEditState]);

  const setEditModeFalse = useCallback(() => {
    setFieldValue("email", data?.payload?.email, true);
    setIsEditState(false);
  }, [setFieldValue, data?.payload?.email]);

  const renderButtonGroup = () => {
    if (isEditState || (disablePrimaryEmailUpdateByUser && !data?.payload?.is_verified)) {
      if (data?.payload?.is_verified) {
        return (
          <Box w="100%" display="flex" justifyContent="space-between">
            <Button
              minHeight="40px"
              height="auto"
              whiteSpace="normal"
              colorScheme="gray"
              variant="outline"
              w={180}
              onClick={setEditModeFalse}
            >
              <FormattedMessage defaultMessage="Cancel" description="Button label" />
            </Button>
            <UpdateEmailButton
              disabled={!values.email || errors?.email !== REQUIRES_EMAIL_VERIFICATION}
              onClick={onOpen}
            />
          </Box>
        );
      } else {
        return (
          <>
            <ExternalEmailModLink primaryEmailPayload={data?.payload} />
            <Flex justifyContent="flex-end">
              <VerifyEmailButton
                disabled={!values.email || errors?.email === INVALID_EMAIL}
                onClick={onOpen}
              />
            </Flex>
          </>
        );
      }
    } else {
      return <NextButton section="email" onClick={onComplete} />;
    }
  };

  return (
    <>
      <Stack spacing={4} p={8}>
        {data?.payload?.is_verified ? (
          <>
            <Text fontSize="lg">
              <FormattedMessage
                defaultMessage="Your email is verified."
                description="Section title"
              />
            </Text>
            <Text fontSize="sm" color="secondaryText">
              <FormattedMessage
                defaultMessage="We will send updates, receipts, tax info, and all communication to the email address we have in our system in accordance with our <link>Privacy Policy</link>."
                description="Privacy policy message with link"
                values={{
                  link: (msg) => (
                    <Link
                      href={env.TILIA_PRIVACY_URL}
                      isExternal
                      color={theme.colors.integrator[400]}
                      fontSize="sm"
                    >
                      {msg}
                    </Link>
                  ),
                }}
              />
            </Text>
            {!disablePrimaryEmailUpdateByUser && (
              <Text fontSize="sm" color="secondaryText">
                <FormattedMessage
                  defaultMessage="If you wish to receive updates to a different email address, please update your email address below."
                  description="User message"
                />
              </Text>
            )}
          </>
        ) : (
          <>
            <Text fontSize="lg">
              <FormattedMessage
                defaultMessage="To start, we need to collect your email"
                description="Section title"
              />
            </Text>
            <Text fontSize="sm" color="secondaryText">
              <FormattedMessage
                defaultMessage="We will use this email to send you updates, receipts, and tax forms in accordance with our <link> Privacy Policy </link>."
                description="Privacy policy message with link"
                values={{
                  link: (msg) => (
                    <Link
                      href={env.TILIA_PRIVACY_URL}
                      isExternal
                      color={theme.colors.integrator[400]}
                      fontSize="sm"
                    >
                      {msg}
                    </Link>
                  ),
                }}
              />
            </Text>
          </>
        )}
        <TextFieldGroup
          label={formatMessage({
            defaultMessage: "Email",
            description: "field label",
          })}
          placeholder={formatMessage({
            defaultMessage: "Email",
            description: "field label",
          })}
          name="email"
          aria-label="email"
          validate={validateEmailField}
          disabled={!isEditState}
          rightElement={
            !disablePrimaryEmailUpdateByUser && !isEditState && (
              <HiOutlinePencil
                data-testid="edit-email-icon"
                cursor="pointer"
                color="tertiaryText"
                onClick={setEditModeTrue}
              />
            )
          }
        />
        {renderButtonGroup()}
      </Stack>
      <EmailVerification
        isOpen={isOpen}
        onClose={onClose}
        onSuccess={onSuccess}
        email={values.email}
      />
    </>
  );
};
